import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { Plus, X } from 'lucide-react';
import { BRAND_PERSON_NAME, BRAND_SITE_URL } from '@/lib/brandConfig';

interface ProductFormData {
  name: string;
  slug: string;
  description: string;
  sku: string;
  price_usd: number;
  price_ars: number;
  product_type: 'recurring' | 'one-time';
  product_category: string;
  billing_interval?: string;
  billing_interval_count: number;
  features: string[];
  tags: string[];
  stock_quantity?: number;
  stock_unlimited: boolean;
  visibility: string;
  is_active: boolean;
  is_featured: boolean;
  button_text: string;
  display_order: number;
  
  // Campos visuales para ReelsToBuy
  video_url: string; // Mux Playback ID
  image_url: string;
  image_alt: string;
  hero_image_url: string;
  thumbnail_url: string;
  cta_button_text: string;
  cta_button_color: string;
  page_title: string;
  page_description: string;
}

export default function CrearProducto() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [currentFeature, setCurrentFeature] = useState('');
  const [currentTag, setCurrentTag] = useState('');

  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    slug: '',
    description: '',
    sku: '',
    price_usd: 0,
    price_ars: 0,
    product_type: 'recurring',
    product_category: 'membership',
    billing_interval: 'monthly',
    billing_interval_count: 1,
    features: [],
    tags: [],
    stock_quantity: undefined,
    stock_unlimited: true,
    visibility: 'public',
    is_active: true,
    is_featured: false,
    button_text: 'Suscribirse',
    display_order: 0,
    
    // Campos visuales
    video_url: '', // Mux Playback ID
    image_url: '',
    image_alt: '',
    hero_image_url: '',
    thumbnail_url: '',
    cta_button_text: 'Comprar Ahora',
    cta_button_color: '#ec4899',
    page_title: '',
    page_description: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Auth: ADMIN_DASHBOARD_API_KEY (Bearer). Usar VITE_ADMIN_DASHBOARD_API_KEY o auth_token.
      const token =
        import.meta.env.VITE_ADMIN_DASHBOARD_API_KEY ||
        localStorage.getItem('auth_token') ||
        sessionStorage.getItem('auth_token');

      if (!token) {
        toast({
          title: 'Error',
          description: 'Falta la API key de admin. Configura VITE_ADMIN_DASHBOARD_API_KEY.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      const response = await fetch('/api/admin/membership-products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al crear producto');
      }

      const isIndividual = formData.product_type === 'one-time' && formData.product_category === 'individual';
      const productUrl = data.productUrl ?? (data.product?.slug ? `${BRAND_SITE_URL}/p/${data.product.slug}` : null);

      toast({
        title: 'Producto creado',
        description: isIndividual && productUrl
          ? `Listo. URL: ${productUrl}`
          : 'El producto se ha creado exitosamente',
      });

      navigate('/admin/productos');
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Error al crear producto',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const addFeature = () => {
    if (currentFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, currentFeature.trim()],
      });
      setCurrentFeature('');
    }
  };

  const removeFeature = (index: number) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index),
    });
  };

  const addTag = () => {
    if (currentTag.trim()) {
      setFormData({
        ...formData,
        tags: [...formData.tags, currentTag.trim()],
      });
      setCurrentTag('');
    }
  };

  const removeTag = (index: number) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <AdminPageHeader
        description="Alta de productos de catálogo (membresía o one-time). Planes VIP DualCTA por país → Mux / país."
      />

      <Card>
        <CardHeader>
          <CardTitle>Crear Nuevo Producto</CardTitle>
          <CardDescription>
            Crea un nuevo producto de membresía o producto individual
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Información Básica */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Información Básica</h3>
              
              <div>
                <Label htmlFor="name">Nombre del Producto *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="slug">Slug (URL)</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="Se genera desde el nombre si está vacío"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Para productos individuales: {new URL(BRAND_SITE_URL).hostname}/p/slug
                </p>
              </div>

              <div>
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="product_type">Tipo de Producto *</Label>
                  <Select
                    value={formData.product_type}
                    onValueChange={(value: 'recurring' | 'one-time') =>
                      setFormData({ ...formData, product_type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recurring">Recurrente (Suscripción)</SelectItem>
                      <SelectItem value="one-time">Pago Único</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="product_category">Categoría *</Label>
                  <Select
                    value={formData.product_category}
                    onValueChange={(value) =>
                      setFormData({ ...formData, product_category: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="membership">Membresía</SelectItem>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="course">Curso</SelectItem>
                      <SelectItem value="digital">Digital</SelectItem>
                      <SelectItem value="physical">Físico</SelectItem>
                      <SelectItem value="service">Servicio</SelectItem>
                      <SelectItem value="other">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="sku">SKU (Código de Producto)</Label>
                <Input
                  id="sku"
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  placeholder="PROD-001"
                />
              </div>
            </div>

            {/* Precios */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Precios</h3>
              <p className="text-sm text-gray-600">Solo se usan USD y ARS</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price_usd">Precio USD *</Label>
                  <Input
                    id="price_usd"
                    type="number"
                    step="0.01"
                    value={formData.price_usd}
                    onChange={(e) => setFormData({ ...formData, price_usd: parseFloat(e.target.value) })}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Para todos los países excepto Argentina</p>
                </div>

                <div>
                  <Label htmlFor="price_ars">Precio ARS</Label>
                  <Input
                    id="price_ars"
                    type="number"
                    step="0.01"
                    value={formData.price_ars}
                    onChange={(e) => setFormData({ ...formData, price_ars: parseFloat(e.target.value) })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Solo para Argentina</p>
                </div>
              </div>
            </div>

            {/* Facturación (solo si es recurrente) */}
            {formData.product_type === 'recurring' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Configuración de Facturación</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="billing_interval">Intervalo de Facturación</Label>
                    <Select
                      value={formData.billing_interval}
                      onValueChange={(value) =>
                        setFormData({ ...formData, billing_interval: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Diario</SelectItem>
                        <SelectItem value="weekly">Semanal</SelectItem>
                        <SelectItem value="monthly">Mensual</SelectItem>
                        <SelectItem value="yearly">Anual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="billing_interval_count">Cantidad de Intervalos</Label>
                    <Input
                      id="billing_interval_count"
                      type="number"
                      min="1"
                      value={formData.billing_interval_count}
                      onChange={(e) =>
                        setFormData({ ...formData, billing_interval_count: parseInt(e.target.value) })
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Características */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Características</h3>
              
              <div className="flex gap-2">
                <Input
                  value={currentFeature}
                  onChange={(e) => setCurrentFeature(e.target.value)}
                  placeholder="Agregar característica"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                />
                <Button type="button" onClick={addFeature}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 bg-secondary px-3 py-1 rounded-md"
                  >
                    <span>{feature}</span>
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="text-destructive hover:text-destructive/80"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Tags</h3>
              
              <div className="flex gap-2">
                <Input
                  value={currentTag}
                  onChange={(e) => setCurrentTag(e.target.value)}
                  placeholder="Agregar tag"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" onClick={addTag}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 bg-primary/10 px-3 py-1 rounded-md"
                  >
                    <span>{tag}</span>
                    <button
                      type="button"
                      onClick={() => removeTag(index)}
                      className="text-destructive hover:text-destructive/80"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Inventario */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Inventario</h3>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="stock_unlimited"
                  checked={formData.stock_unlimited}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, stock_unlimited: checked })
                  }
                />
                <Label htmlFor="stock_unlimited">Stock Ilimitado</Label>
              </div>

              {!formData.stock_unlimited && (
                <div>
                  <Label htmlFor="stock_quantity">Cantidad en Stock</Label>
                  <Input
                    id="stock_quantity"
                    type="number"
                    min="0"
                    value={formData.stock_quantity || 0}
                    onChange={(e) =>
                      setFormData({ ...formData, stock_quantity: parseInt(e.target.value) })
                    }
                  />
                </div>
              )}
            </div>

            {/* Video (Mux Playback ID) */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Video</h3>
              <p className="text-sm text-gray-600">Mux Playback ID para el video del producto</p>
              
              <div>
                <Label htmlFor="video_url">Mux Playback ID</Label>
                <Input
                  id="video_url"
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  placeholder="abc123xyz (solo el ID, no la URL completa)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ejemplo: Si tu video en Mux es "abc123xyz", solo pon "abc123xyz"
                </p>
              </div>
            </div>

            {/* Imágenes y Contenido Visual */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Imágenes</h3>
              <p className="text-sm text-gray-600">Imágenes para el producto (portada, hero, thumbnail)</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="image_url">URL Imagen Principal</Label>
                  <Input
                    id="image_url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://res.cloudinary.com/.../image.jpg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Portada del video o imagen principal</p>
                </div>

                <div>
                  <Label htmlFor="image_alt">Texto Alternativo</Label>
                  <Input
                    id="image_alt"
                    value={formData.image_alt}
                    onChange={(e) => setFormData({ ...formData, image_alt: e.target.value })}
                    placeholder="Descripción de la imagen"
                  />
                </div>

                <div>
                  <Label htmlFor="hero_image_url">URL Imagen Hero</Label>
                  <Input
                    id="hero_image_url"
                    value={formData.hero_image_url}
                    onChange={(e) => setFormData({ ...formData, hero_image_url: e.target.value })}
                    placeholder="https://res.cloudinary.com/.../hero.jpg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Imagen grande para pantalla de compra</p>
                </div>

                <div>
                  <Label htmlFor="thumbnail_url">URL Thumbnail</Label>
                  <Input
                    id="thumbnail_url"
                    value={formData.thumbnail_url}
                    onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                    placeholder="https://res.cloudinary.com/.../thumb.jpg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Thumbnail para listados</p>
                </div>
              </div>
            </div>

            {/* Personalización del Botón */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Personalización del Botón</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cta_button_text">Texto del Botón CTA</Label>
                  <Input
                    id="cta_button_text"
                    value={formData.cta_button_text}
                    onChange={(e) => setFormData({ ...formData, cta_button_text: e.target.value })}
                    placeholder="Comprar Ahora"
                  />
                </div>

                <div>
                  <Label htmlFor="cta_button_color">Color del Botón (Hex)</Label>
                  <div className="flex gap-2">
                    <Input
                      id="cta_button_color"
                      value={formData.cta_button_color}
                      onChange={(e) => setFormData({ ...formData, cta_button_color: e.target.value })}
                      placeholder="#ec4899"
                    />
                    <div 
                      className="w-12 h-10 rounded border"
                      style={{ backgroundColor: formData.cta_button_color }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* SEO y Metadata */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">SEO y Metadata</h3>
              
              <div>
                <Label htmlFor="page_title">Título de la Página</Label>
                <Input
                  id="page_title"
                  value={formData.page_title}
                  onChange={(e) => setFormData({ ...formData, page_title: e.target.value })}
                  placeholder={`Video Personalizado | ${BRAND_PERSON_NAME}`}
                />
              </div>

              <div>
                <Label htmlFor="page_description">Descripción de la Página</Label>
                <Textarea
                  id="page_description"
                  value={formData.page_description}
                  onChange={(e) => setFormData({ ...formData, page_description: e.target.value })}
                  placeholder="Descripción para SEO y redes sociales"
                  rows={2}
                />
              </div>
            </div>

            {/* Configuración de Visualización */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Configuración de Visualización</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="visibility">Visibilidad</Label>
                  <Select
                    value={formData.visibility}
                    onValueChange={(value) => setFormData({ ...formData, visibility: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Público</SelectItem>
                      <SelectItem value="private">Privado</SelectItem>
                      <SelectItem value="hidden">Oculto</SelectItem>
                      <SelectItem value="draft">Borrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="button_text">Texto del Botón</Label>
                  <Input
                    id="button_text"
                    value={formData.button_text}
                    onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="display_order">Orden de Visualización</Label>
                  <Input
                    id="display_order"
                    type="number"
                    value={formData.display_order}
                    onChange={(e) =>
                      setFormData({ ...formData, display_order: parseInt(e.target.value) })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_active: checked })
                    }
                  />
                  <Label htmlFor="is_active">Activo</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_featured"
                    checked={formData.is_featured}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_featured: checked })
                    }
                  />
                  <Label htmlFor="is_featured">Destacado</Label>
                </div>
              </div>
            </div>

            {/* Botones */}
            <div className="flex gap-4">
              <Button type="submit" disabled={loading}>
                {loading ? 'Creando...' : 'Crear Producto'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/productos')}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
